import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Link,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { MenuBook as BookIcon } from '@mui/icons-material';
import { signIn, getSavedCredentials } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import { SEO } from '../utils/seo/SEO';

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser, loading: authLoading } = useAuth();
  
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [bookOpening, setBookOpening] = useState<boolean>(false);

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/home');
    }
  }, [currentUser, navigate]);

  // Load saved email if available (password is never stored)
  useEffect(() => {
    const { email: savedEmail, rememberMe: savedRememberMe } = getSavedCredentials();

    if (savedRememberMe && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(savedRememberMe);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      await signIn(email, password, rememberMe);
      
      // Trigger book opening animation
      setBookOpening(true);
      
      // Navigate after animation
      setTimeout(() => {
        navigate('/home');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in. Please check your credentials.');
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
        }}
      >
        <CircularProgress 
          sx={{ 
            color: theme.palette.mode === 'dark' 
              ? 'rgba(255, 138, 101, 0.8)' 
              : 'rgba(139, 69, 19, 0.8)' 
          }} 
        />
      </Box>
    );
  }

  return (
    <>
      <SEO
        title="Login"
        description="Log in to Cook with Otto to access your AI-powered cooking assistant and personalized recipes."
        url="/login"
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
          : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
        p: { xs: 2, md: 4 },
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, type: 'spring', damping: 25 }}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '450px',
          perspective: '1500px',
        }}
      >
        {/* Book Cover Container */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '600px',
            transformStyle: 'preserve-3d',
            transformOrigin: 'left center',
            transition: 'transform 2s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: bookOpening ? 'rotateY(-180deg)' : 'rotateY(0deg)',
          }}
        >
          {/* Front Cover with Login Form */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              background: 'linear-gradient(135deg, #654321 0%, #8B4513 100%)',
              borderRadius: '8px 12px 12px 8px',
              border: '3px solid #4A2C17',
              boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              p: 4,
              overflow: 'hidden',
            }}
          >
            {/* Book Spine */}
            <Box
              sx={{
                position: 'absolute',
                left: -3,
                top: 0,
                bottom: 0,
                width: '15px',
                background: 'linear-gradient(to bottom, #4A2C17 0%, #2C1810 100%)',
                borderRadius: '8px 0 0 8px',
                boxShadow: 'inset -2px 0 4px rgba(0,0,0,0.3)',
              }}
            />

            {/* Title Section */}
            <Box sx={{ textAlign: 'center', color: '#FFF8F0' }}>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <BookIcon 
                  sx={{ 
                    fontSize: 60, 
                    mb: 2,
                    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
                  }} 
                />
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  }}
                >
                  Cook with Otto
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: '"Crimson Text", serif',
                    fontStyle: 'italic',
                    opacity: 0.9,
                    mb: 2,
                  }}
                >
                  Recipe Collection
                </Typography>
              </motion.div>

              {/* Decorative line */}
              <Box sx={{ 
                width: 100,
                height: 2,
                backgroundColor: '#FFF8F0',
                margin: '0 auto',
                opacity: 0.5,
              }} />
            </Box>

            {/* Login Form Section */}
            <Box 
              component="form" 
              onSubmit={handleLogin}
              sx={{
                background: 'rgba(255, 248, 240, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '2px solid rgba(255, 248, 240, 0.2)',
                p: 3,
                mt: 2,
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600,
                  textAlign: 'center',
                  color: '#FFF8F0',
                  mb: 2,
                }}
              >
                Unlock Your Recipe Book
              </Typography>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'rgba(255, 138, 101, 0.1)',
                    border: '1px solid rgba(255, 138, 101, 0.3)',
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Email Field */}
              <TextField
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="Your email"
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 248, 240, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 248, 240, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 248, 240, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(255, 248, 240, 0.7)',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontFamily: '"Crimson Text", serif',
                    fontSize: '1rem',
                  },
                }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="Your secret key"
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 248, 240, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 248, 240, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 248, 240, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(255, 248, 240, 0.7)',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontFamily: '"Crimson Text", serif',
                    fontSize: '1rem',
                  },
                }}
              />

              {/* Remember Me */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    size="small"
                    sx={{
                      color: 'rgba(255, 248, 240, 0.7)',
                      '&.Mui-checked': {
                        color: 'rgba(255, 248, 240, 0.9)',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ 
                    fontFamily: '"Crimson Text", serif',
                    color: '#FFF8F0',
                    fontSize: '0.9rem',
                  }}>
                    Keep me signed in
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              {/* Open Book Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !email || !password}
                sx={{
                  fontFamily: '"Crimson Text", serif',
                  fontSize: '1.1rem',
                  py: 1,
                  background: 'rgba(255, 248, 240, 0.9)',
                  color: '#654321',
                  border: '2px solid rgba(255, 248, 240, 0.7)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'rgba(255, 248, 240, 1)',
                  },
                  '&:disabled': {
                    background: 'rgba(255, 248, 240, 0.3)',
                    color: 'rgba(101, 67, 33, 0.5)',
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#654321' }} />
                    Opening...
                  </>
                ) : (
                  'Open Recipe Book'
                )}
              </Button>
            </Box>

            {/* Links */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
            }}>
              <Link 
                component={RouterLink} 
                to="#" 
                sx={{ 
                  fontFamily: '"Crimson Text", serif',
                  color: 'rgba(255, 248, 240, 0.7)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'rgba(255, 248, 240, 0.9)',
                  },
                }}
              >
                Forgot password?
              </Link>
              <Link 
                component={RouterLink} 
                to={ROUTES.SIGNUP}
                sx={{ 
                  fontFamily: '"Crimson Text", serif',
                  color: 'rgba(255, 248, 240, 0.7)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'rgba(255, 248, 240, 0.9)',
                  },
                }}
              >
                New chef? Join us
              </Link>
            </Box>
          </Box>

          {/* Back Cover (visible when book opens) */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              background: '#FFFEF8',
              borderRadius: '12px 8px 8px 12px',
              border: '3px solid #E6D3B7',
              boxShadow: 'inset 0 0 20px rgba(139, 69, 19, 0.1)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 600,
                color: '#8B4513',
                textAlign: 'center',
              }}
            >
              Welcome to Your Kitchen
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </Box>
    </>
  );
};

export default LoginScreen;